<template>
  <div class="filter-distribution">
    <Accordion>
      <AccordionItem item-id="filter-distribution">
        <template #trigger>
          <div class="flex items-center justify-between px-2 w-full grid text-sm">
            <span class="font-medium">Filter Distribution</span>
            <span class="text-muted-foreground ml-auto">{{ filteredVehiclesCount }} vehicles</span>
          </div>
        </template>
        
        <div class="pt-2 space-y-4">
          <!-- Weekly Payment Distribution (formerly Price Distribution) -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium">Weekly Payment Range</span>
            </div>
            <div class="h-6 bg-secondary/30 rounded-md overflow-hidden relative">
              <!-- Weekly Payment Distribution Bars -->
              <div 
                v-for="(segment, index) in priceDistribution" 
                :key="index"
                class="absolute top-0 bottom-0 bg-primary/60"
                :style="{
                  left: `${segment.start}%`,
                  width: `${segment.width}%`,
                  opacity: segment.opacity
                }"
              ></div>
              
              <!-- Weekly Payment Range Indicator (with added safety check) -->
              <div 
                 v-if="activeFilters && activeFilters.perweek" 
                 class="absolute top-0 bottom-0 border-2 border-primary rounded-md"
                 :style="{
                   left: `${getPricePosition(activeFilters.perweek.min)}%`,
                   width: `${getPricePosition(activeFilters.perweek.max) - getPricePosition(activeFilters.perweek.min)}%`
                 }"
              ></div>
            </div>
            <div class="flex justify-between text-xs text-muted-foreground mt-1">
              <span>${{ minWeekly.toLocaleString() }}/week</span>
              <span>${{ maxWeekly.toLocaleString() }}/week</span>
            </div>
          </div>
          
          <!-- Body Type Distribution -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium">Body Types</span>
            </div>
            <div class="grid grid-cols-5 gap-1">
              <div 
                v-for="(type, index) in bodyTypeDistribution" 
                :key="index"
                class="flex flex-col items-center"
              >
                <div class="h-16 w-full flex items-end justify-center">
                  <div 
                    class="w-full bg-primary/60 rounded-t-sm"
                    :class="{ 'bg-primary': isBodyTypeSelected(type.value) }"
                    :style="{ height: `${type.percentage}%` }"
                  ></div>
                </div>
                <span class="text-xs text-center mt-1 truncate w-full" :title="type.label">{{ type.label }}</span>
                <span class="text-xs text-muted-foreground">{{ type.count }}</span>
              </div>
            </div>
          </div>
          
          <!-- Transmission Distribution -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium">Transmission</span>
            </div>
            <div class="flex gap-1 h-8">
              <div 
                v-for="(type, index) in transmissionDistribution" 
                :key="index"
                class="h-full rounded-sm"
                :class="{ 'bg-primary': isTransmissionSelected(type.value), 'bg-primary/60': !isTransmissionSelected(type.value) }"
                :style="{ width: `${type.percentage}%` }"
                :title="`${type.label}: ${type.count} vehicles (${type.percentage.toFixed(1)}%)`"
              ></div>
            </div>
            <div class="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Automatic</span>
              <span>Manual</span>
            </div>
          </div>

          <!-- Condition Distribution (Added) -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium">Condition</span>
            </div>
            <div class="grid grid-cols-3 gap-2"> <!-- Adjust grid columns as needed -->
              <div 
                v-for="(type, index) in conditionDistribution" 
                :key="index"
                class="flex flex-col items-center"
              >
                <div class="h-16 w-full flex items-end justify-center">
                  <div 
                    class="w-full bg-primary/60 rounded-t-sm"
                     :class="{ 'bg-primary': isConditionSelected(type.value) }"
                    :style="{ height: `${type.percentage}%` }"
                  ></div>
                </div>
                <span class="text-xs text-center mt-1 truncate w-full" :title="type.label">{{ type.label }}</span>
                <span class="text-xs text-muted-foreground">{{ type.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </AccordionItem>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useVehiclesPageFilters } from '~/stores/vehiclesPageFilters'
import { Accordion, AccordionItem } from '~/layers/base/components/ui/accordion'
import { calculateWeeklyPayment } from '~/layers/base/utils/finance'
import type { ValueDisplayPair } from '~/types'

interface FilterOptionBasic {
    value: string;
    label?: string;
}

interface DistributionSegment {
  start: number
  width: number
  count: number
  percentage: number
  opacity: number
}

interface DistributionItem {
  count: number
  label: string
  value: string
  percentage: number
}

const vehiclesStore = useVehiclesStore()
const vehiclesPageFilters = useVehiclesPageFilters()

const activeFilters = computed(() => vehiclesPageFilters.activeFilters || {})

const filteredVehiclesCount = computed(() => {
  return vehiclesStore.filteredVehicles.length
})

const minWeekly = computed(() => {
  const minPrice = vehiclesStore.availableFilters.priceRange?.min ?? 0;
  return calculateWeeklyPayment(minPrice);
});

const maxWeekly = computed(() => {
   const maxPrice = vehiclesStore.availableFilters.priceRange?.max ?? 100000;
   return calculateWeeklyPayment(maxPrice);
});

const priceDistribution = computed((): DistributionSegment[] => {
  const vehicles = vehiclesStore.vehicles;
  const minW = minWeekly.value;
  const maxW = maxWeekly.value;
  const range = maxW - minW;
  
  if (range <= 0 || vehicles.length === 0) return []; 

  const segments = 5;
  const segmentSize = range / segments;
  const distribution = Array(segments).fill(0).map((_, i) => {
    const segmentMin = minW + (i * segmentSize);
    const segmentMax = segmentMin + segmentSize;
    
    const count = vehicles.filter((v: any) => { 
      const weeklyValue = Number(v.perweek ?? 0);
      return weeklyValue >= segmentMin && weeklyValue < segmentMax;
    }).length;
    
    const percentage = (vehicles.length > 0) ? (count / vehicles.length) * 100 : 0;
    
    return {
      start: (i / segments) * 100,
      width: 100 / segments,
      count,
      percentage,
      opacity: 0.3 + (percentage / 100) * 0.7 
    };
  });
  
  return distribution;
});

const getPricePosition = (weeklyValue: number | undefined | null): number => {
   const val = Number(weeklyValue ?? 0);
   const minW = minWeekly.value;
   const maxW = maxWeekly.value;
   const range = maxW - minW;
   if (range <= 0) return 0; 
   const clampedVal = Math.max(minW, Math.min(val, maxW)); 
   return ((clampedVal - minW) / range) * 100;
};

const bodyTypeDistribution = computed((): DistributionItem[] => {
  const vehicles = vehiclesStore.vehicles; 
  const bodyTypes = new Map<string, { count: number; label: string }>();
  
  vehicles.forEach((vehicle: any) => {
    const bodyValue = vehicle.body?.value?.[0];
    const bodyLabel = vehicle.body?.displayValue?.[0] || bodyValue;
    if (bodyValue && bodyLabel) {
      const current = bodyTypes.get(bodyValue) || { count: 0, label: bodyLabel };
      current.count++;
      bodyTypes.set(bodyValue, current);
    }
  });
  
  const totalVehicles = vehicles.length;
  if (totalVehicles === 0) return [];

  return Array.from(bodyTypes.entries()).map(([value, data]) => ({
    value,
    label: data.label,
    count: data.count,
    percentage: (data.count / totalVehicles) * 100
  }));
});

const isBodyTypeSelected = (value: string): boolean => {
  const currentFilter = activeFilters.value.body;
  return Array.isArray(currentFilter) && currentFilter.includes(value);
};

const transmissionDistribution = computed((): DistributionItem[] => {
   const vehicles = vehiclesStore.vehicles; 
   const transmissions = new Map<string, { count: number; label: string }>();
   
   vehicles.forEach((vehicle: any) => {
     const transValue = vehicle.transmission?.value?.[0];
     const transLabel = vehicle.transmission?.displayValue?.[0] || transValue;
     if (transValue && transLabel) {
       const current = transmissions.get(transValue) || { count: 0, label: transLabel };
       current.count++;
       transmissions.set(transValue, current);
     }
   });

   const totalVehicles = vehicles.length;
   if (totalVehicles === 0) return [];
 
   return Array.from(transmissions.entries()).map(([value, data]) => ({
     value,
     label: data.label,
     count: data.count,
     percentage: (data.count / totalVehicles) * 100
   }));
});

const isTransmissionSelected = (value: string): boolean => {
   const currentFilter = activeFilters.value.transmission;
   return Array.isArray(currentFilter) && currentFilter.includes(value);
};

// --- Condition Distribution Logic (Added) ---
const conditionDistribution = computed((): DistributionItem[] => {
   const vehicles = vehiclesStore.vehicles; 
   const conditions = new Map<string, { count: number; label: string }>();
   
   vehicles.forEach((vehicle: any) => {
     const condValue = vehicle.condition?.value?.[0];
     const condLabel = vehicle.condition?.displayValue?.[0] || condValue; // Use display value
     if (condValue && condLabel) {
       const current = conditions.get(condValue) || { count: 0, label: condLabel };
       current.count++;
       conditions.set(condValue, current);
     }
   });

   const totalVehicles = vehicles.length;
   if (totalVehicles === 0) return [];
 
   // Sort maybe? New, Demo, Used?
   const sortedConditions = Array.from(conditions.entries())
        .sort(([, a], [, b]) => {
             // Basic sort: New > Demo > Used
             const order: Record<string, number> = { new: 1, demo: 2, used: 3 };
             return (order[a.label.toLowerCase()] || 99) - (order[b.label.toLowerCase()] || 99);
         });

   return sortedConditions.map(([value, data]) => ({
     value,
     label: data.label,
     count: data.count,
     percentage: (data.count / totalVehicles) * 100
   }));
});

// Check if a condition is selected (Added)
const isConditionSelected = (value: string): boolean => {
   const currentFilter = activeFilters.value.condition; // Check 'condition' key
   return Array.isArray(currentFilter) && currentFilter.includes(value);
};
</script>
